import React, { useEffect, useState } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import { PrevBtn, NextBtn, PaginationWrapper, Btn } from "../../Styles/PageStyles";
import Images from "../../../Images";
import tw from "tailwind-styled-components";
import { ownership_roles, PaginationLimit } from "../../../Config";
import { IoCopySharp, IoEye } from "react-icons/io5";
import moment from "moment";
import { capitalizeFirstLetter, handleCopy, truncateText } from "../../../helpers";
import { FiChevronRight } from "react-icons/fi";
import { IoMdMore } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import { MdOutlineChevronRight } from "react-icons/md";
import Tooltip from "../../Tooltip";

const LandlordLeaseTable = ({
    leaseDetails,
    setSelectedData,
    setViewDetails,
}) => {
    const storageKey = `landlord_lease_page`;
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [selectedRow, setSelectedRow] = useState(null);
    const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
    const [openActionMenu, setOpenActionMenu] = useState(null);
    const [previousId, setPreviousId] = useState(null);

    // Load saved page index
    const storageValue = sessionStorage.getItem(storageKey);
    const savedPage = storageValue !== null ? Number(storageValue) : 0;
    const [currentPage, setCurrentPage] = useState(savedPage);

    useEffect(() => {
        setCurrentPage(savedPage);
    }, [savedPage]);

    const toggleRow = (index) => {
        setSelectedRow(selectedRow === index ? null : index);
    };
    const getOwnershipLabel = (ownershipValue) => {
        const ownership = ownership_roles.find(
            (role) => role.value === ownershipValue
        );
        return ownership ? ownership.label : "Unknown";
    };
    const viewDetail = (item) => {
        setViewDetails(true);
        setSelectedData(item);
        setOpenActionMenu(null);
    };

    const ActionBtns = (item) => {
        return (
            <div className="flex items-center gap-3">
                <Tooltip label="Click to view lease details">
                    <button
                        onClick={() => {
                            viewDetail(item);
                        }}
                        className="px-4 ml-auto text-gray-400 hover:text-gray-700 transition"
                    >
                        <MdOutlineChevronRight size={22} />
                    </button>
                </Tooltip>
            </div>
        );
    };


    const getName = (item) => {
        const name = capitalizeFirstLetter(item?.landlordName || "N/A");

        return (
            <UserImageWrapper onClick={() => viewDetail(item)}>
                <UserImage src={Images.Avatar} alt={name} />
                <div className="flex flex-col min-w-0">
                    <span className="text-sm font-medium text-gray-900 truncate max-w-[180px]">
                        {name}
                    </span>
                    <div className="flex items-center gap-1 text-xs text-gray-400">
                        <span>#{item.landlordMobile}</span>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleCopy(item.landlordMobile);
                            }}
                            className="hover:text-gray-600 transition"
                            title="Copy number"
                        >
                            <IoCopySharp size={12} />
                        </button>
                    </div>
                </div>
            </UserImageWrapper>
        );
    };

    const getFlatInfo = (item) => {
        return (
            <div className="flex flex-col">
                <span className="text-sm font-medium text-gray-900">
                    {item.flatName ? `Flat ${item.flatName}` : "--"}
                </span>
            </div>
        );
    };

    useEffect(() => {
        if (leaseDetails && leaseDetails.length > 0) {
            const tempData = leaseDetails.map((item) => ({
                id: item.id,
                name: getName(item),
                email: item.landlordEmail || "--",
                ownership: getOwnershipLabel(item.ownershipType),
                flat: getFlatInfo(item),
                action: ActionBtns(item),
            }));
            setData(tempData);
        }
    }, [leaseDetails, openActionMenu]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                !event.target.closest("#menuOptions") &&
                !event.target.closest(".action-btn")
            ) {
                setOpenActionMenu(null);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const handleResize = () => {
            setIsMobileView(window.innerWidth < 768);
        };

        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const columns = React.useMemo(() => {
        if (isMobileView) {
            return [
                {
                    Header: "Landlord",
                    accessor: "name",
                },
                {
                    Header: "Flat",
                    accessor: "flat",
                },
            ];
        }
        return [
            {
                Header: "Landlord Name",
                accessor: "name",
            },
            {
                Header: "Email",
                accessor: "email",
            },
            {
                Header: "Flat",
                accessor: "flat",
            },
            {
                Header: "Ownership",
                accessor: "ownership",
            },

            {
                Header: "",
                accessor: "action",
            },
        ];
    }, [isMobileView]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        pageOptions,
        page,
        state: { pageIndex },
        previousPage,
        nextPage,
        canPreviousPage,
        canNextPage,
    } = useTable(
        {
            data,
            columns,
            initialState: { pageIndex: currentPage, pageSize: PaginationLimit },
        },
        useSortBy,
        usePagination
    );

    useEffect(() => {
        sessionStorage.setItem(storageKey, pageIndex);
    }, [pageIndex, storageKey]);

    return (
        <>
            <CustomTable {...getTableProps()}>
                <Thead>
                    {headerGroups.map((headerGroup, idx) => (
                        <Tr {...headerGroup.getHeaderGroupProps()} key={idx}>
                            {headerGroup.headers.map((column, colIdx) => (
                                <Th {...column.getHeaderProps(column.getSortByToggleProps())} key={colIdx}>
                                    {column.render("Header")}
                                    <span>
                                        {column.isSorted ? (
                                            <img
                                                src={Images.Arrow}
                                                alt="down arrow"
                                                className={`${column.isSortedDesc ? "-rotate-90" : "rotate-90"
                                                    } w-1.5 inline-block ml-1.5`}
                                            />
                                        ) : (
                                            ""
                                        )}
                                    </span>
                                </Th>
                            ))}
                        </Tr>
                    ))}
                </Thead>
                <Tbody {...getTableBodyProps()} hasdata={(leaseDetails?.length > 0).toString()}>
                    {page.map((row, rowIndex) => {
                        prepareRow(row);
                        const originalRow = leaseDetails?.[rowIndex];
                        return (
                            <React.Fragment key={rowIndex}>
                                <Tr
                                    {...row.getRowProps({ style: { height: "80px" } })}
                                    hasdata={(leaseDetails?.length > 0).toString()}
                                >
                                    {row.cells.map((cell, cellIndex) => {
                                        return (
                                            <Td
                                                {...cell.getCellProps()}
                                                onClick={() => {
                                                    if (
                                                        cellIndex === 0 ||
                                                        (cellIndex === 1 && window.innerWidth < 768)
                                                    ) {
                                                        toggleRow(rowIndex);
                                                    }
                                                }}
                                            >
                                                {cell.render("Cell")}
                                            </Td>
                                        );
                                    })}
                                </Tr>
                                {selectedRow === rowIndex && window.innerWidth < 768 && (
                                    <Tr>
                                        <Td colSpan={columns.length}>
                                            <ul className="flex flex-col gap-2">
                                                <li>
                                                    <strong>Email:</strong> {data[rowIndex]?.email}
                                                </li>
                                                <li>
                                                    <strong>Ownership:</strong> {data[rowIndex]?.ownership}
                                                </li>
                                                <li>
                                                    <strong>Rent:</strong> ₹{originalRow?.rent || 0}
                                                </li>
                                                <li>
                                                    <strong>Security:</strong> ₹{originalRow?.security || 0}
                                                </li>
                                                <li>
                                                    <strong>Duration:</strong>{" "}
                                                    {originalRow?.startDate ? moment(originalRow.startDate).format("DD MMM YY") : "-"}
                                                    {" - "}
                                                    {originalRow?.endDate ? moment(originalRow.endDate).format("DD MMM YY") : "-"}
                                                </li>
                                            </ul>
                                        </Td>
                                    </Tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </Tbody>
            </CustomTable>

            {leaseDetails?.length > 0 && (
                <PaginationWrapper>
                    <div className="px-2">
                        Page{" "}
                        <em>
                            {pageIndex + 1} of {pageOptions.length}
                        </em>
                    </div>
                    <div className="flex gap-1">
                        <PrevBtn onClick={() => previousPage()} disabled={!canPreviousPage}>
                            <img src={Images.Arrow} alt="arrow" className="w-2 opacity-75" />
                        </PrevBtn>
                        <NextBtn onClick={() => nextPage()} disabled={!canNextPage}>
                            <img
                                src={Images.Arrow}
                                alt="arrow"
                                className="w-2 rotate-180 opacity-75"
                            />
                        </NextBtn>
                    </div>
                </PaginationWrapper>
            )}
        </>
    );
};

// Styled Components
const CustomTable = tw.table`w-full text-gray-600`;
const Thead = tw.thead`border-b uppercase text-gray-600 bg-blue-50`;
const Tbody = tw.tbody`${(props) => (props.hasdata === "true" ? "border-b" : "")}`;
const Tr = tw.tr`${(props) =>
    props.hasdata === "true"
        ? "border-b"
        : ""} rounded-md overflow-hidden hover:bg-slate-50`;
const Th = tw.th`text-left text-sm md:p-3 p-0.5 font-normal text-gray-600`;
const Td = tw.td`p-3 text-sm md:p-3 text-left`;
const UserImageWrapper = tw.div`flex items-center gap-2 cursor-pointer`;
const UserImage = tw.img`w-10 h-10 rounded-full`;
const ActionBtnWrapper = tw.div`flex items-center gap-3 relative`;

const MenuItem = tw.div`
  flex items-center gap-2 
  px-4 py-2 
  text-sm 
  cursor-pointer 
  hover:bg-gray-50
`;
const MenuOptions = tw.div`
  absolute bottom-10 right-0
  py-2 w-44
  bg-white z-20
  rounded-xl
  shadow-xl
  border border-gray-200
  flex flex-col
`;

export default LandlordLeaseTable;